/*
 * medallion-orphan-sync.js
 *
 * Charlie 2026-06-11 v5 (Mike P0 hardening, second attempt):
 *
 * Personalised Medallion lines are added as companion line items to their
 * parent CHUUG vessel via the BYC ATC handler. When a parent vessel is
 * removed OR its PERSONALISED COIN property is cleared (cart drawer, /cart
 * Remove button, perso prompt swap, an earlier theme version that didn't
 * add `_bundled_with` at all), the medallion line is left orphaned — a
 * real cart line item with no parent.
 *
 * v3/v4 (earlier today) detected orphans by matching `_bundled_with` ↔
 * `_cart_unique` line-item properties. That FAILED in production for
 * Mike's cart — checkout still surfaced 6 orphan medallions (A, E, Z, W,
 * F + an extra DAD) for a cart with 2 vessels (only one with
 * PERSONALISED COIN = DAD). Hypothesis: the orphan medallions in his
 * cart predate the `_bundled_with` convention, or the property was
 * stripped somewhere along the way. Either way the parent-child link is
 * gone, so the old detector returned 0 orphans and walked away.
 *
 * v5 — switch to COUNT-MATCH detection. We don't try to be clever about
 * parent-child linkage. Instead:
 *
 *   • Enumerate vessel lines: any cart line whose product.handle is NOT
 *     `personalised-medallion` AND that exposes a PERSONALISED COIN
 *     line-item property. The property's value (uppercased) is the
 *     letter that line WANTS. Vessels with PERSONALISED COIN ∈ {
 *     '', 'NONE', 'STANDARD' } don't want a medallion.
 *
 *   • Build a `wanted` map: { letter → wanted_qty } by summing the
 *     quantity of vessel lines that want each letter.
 *
 *   • Enumerate medallion lines: handle === 'personalised-medallion'.
 *     The letter is derived from variant_id via a hardcoded reverse
 *     map (LETTER_BY_VARIANT) — variant id table copied verbatim from
 *     sections/build-your-chuug-legacy.liquid's MEDALLION_VARIANTS
 *     constant so it can't drift.
 *
 *   • Build an `actual` map: { letter → actual_qty } for medallions
 *     currently in cart.
 *
 *   • For every letter that appears in actual but with actual > wanted,
 *     issue POST /cart/change.js { id: variantId, quantity: wanted_qty }.
 *     With id = variant ID, Shopify sets the ABSOLUTE quantity for that
 *     variant. If wanted_qty is 0/undefined we remove the line entirely;
 *     if wanted_qty is 2 and actual_qty is 5, we trim to 2. This is the
 *     simplest correct cleanup.
 *
 * Verbose logging: every cleanupNow() call emits a stats block at the
 * start (vessel count, wants map, actual map) and one line per mutation
 * decision. Mike can paste DevTools console output back as evidence.
 *
 * cleanupNow() is awaited by:
 *   1. The Secure Checkout submit handler (main-cart.liquid)
 *   2. The express-checkout pointerdown handler (main-cart.liquid)
 *   3. The cart-page-load IIFE (main-cart.liquid)
 *   4. BYC ATC after a vessel is removed mid-flow
 *
 * Single-flight: concurrent callers share an in-flight Promise.
 * Idempotent: if cleanupNow runs and finds nothing to do, it resolves
 * to 0 instantly.
 *
 * Never throws: every fetch is try/catch'd. Cleanup failure NEVER
 * blocks checkout — better a sub-optimal checkout than a totally
 * stuck one.
 */
(function(){
  'use strict';

  // ── Hardcoded medallion variant table (mirror of MEDALLION_VARIANTS
  //    in sections/build-your-chuug-legacy.liquid). letter → variantId.
  //    If you change this table you must also update the BYC builder.
  var MEDALLION_VARIANT_BY_LETTER = {
    A:   56288183320955,
    B:   56288182436219,
    C:   56288182468987,
    D:   56288182501755,
    E:   56288182534523,
    F:   56288182567291,
    G:   56288182600059,
    H:   56288182632827,
    I:   56288182665595,
    J:   56288182698363,
    K:   56288182731131,
    L:   56288182763899,
    M:   56288182796667,
    N:   56288182829435,
    O:   56288182862203,
    P:   56288182894971,
    Q:   56288182927739,
    R:   56288182960507,
    S:   56288182993275,
    T:   56288183026043,
    U:   56288183058811,
    V:   56288183091579,
    W:   56288183124347,
    X:   56288183157115,
    Y:   56288183189883,
    Z:   56288183222651,
    DAD: 56288183255419
  };
  // Reverse map: variantId (string) → letter. Build once at load.
  var LETTER_BY_VARIANT = (function(){
    var out = Object.create(null);
    Object.keys(MEDALLION_VARIANT_BY_LETTER).forEach(function(letter){
      out[String(MEDALLION_VARIANT_BY_LETTER[letter])] = letter;
    });
    return out;
  })();

  // ── State ─────────────────────────────────────────────────────────────
  var syncing = false;
  var syncQueued = false;
  var debounceTimer = null;
  var DEBOUNCE_MS = 100;     // run AFTER gift-box-sync's 60ms so its mutations have settled
  var inflightInternal = 0;
  var inflightCleanup = null;   // shared in-flight Promise for cleanupNow() callers

  window.CHUUG_MEDALLION_ORPHAN_SYNC = {
    sync: scheduleSync,
    cleanupNow: cleanupNow,
    beginExternalSwap: function(){ inflightInternal++; },
    endExternalSwap:   function(){ inflightInternal = Math.max(0, inflightInternal - 1); },
    // Charlie 2026-06-11 PM dropdown-bug fix: exposed so external code
    // (main-cart.liquid inline IIFE, dropdown handler, etc.) can check
    // whether ANY external swap is in flight and skip its own reload
    // accordingly. Inside doSync this is checked directly via the
    // closure variable; this getter is for code outside the module.
    isExternalSwapBusy: function(){ return inflightInternal > 0; },
    // Charlie 2026-06-11 PM: exposed for main-cart.liquid in-cart
    // perso dropdown handler so it can mint the correct medallion
    // companion line when the customer picks a letter from the cart.
    // Single source of truth — don't duplicate the variant table.
    variantByLetter: MEDALLION_VARIANT_BY_LETTER
  };

  // ── Helpers ───────────────────────────────────────────────────────────
  function isInternalFetch(){ return inflightInternal > 0; }

  // Charlie 2026-06-29 (Audit Finding #2 — /cart.js storm dedupe):
  // Shared with gift-box-sync.js via window.CHUUG_CART_CACHE. If neither
  // script ran first we install the cache here too (defensive — load order
  // could in theory flip on a future template). 250ms window collapses
  // overlapping GETs into a single promise so cart-sync chains can't
  // hammer /cart.js into a 429. POSTs are NOT cached.
  if (!window.CHUUG_CART_CACHE){
    window.CHUUG_CART_CACHE = (function(){
      var inflight = null;
      var expiresAt = 0;
      var WINDOW_MS = 250;
      function get(){
        var now = Date.now();
        if (inflight && now < expiresAt) return inflight;
        inflight = fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function(r){
            if (!r.ok) throw new Error('HTTP ' + r.status + ' on /cart.js');
            return r.json();
          });
        expiresAt = now + WINDOW_MS;
        inflight.catch(function(){}).then(function(){
          setTimeout(function(){
            if (Date.now() >= expiresAt) inflight = null;
          }, WINDOW_MS);
        });
        return inflight;
      }
      function bust(){ inflight = null; expiresAt = 0; }
      return { get: get, bust: bust };
    })();
  }

  function fetchJSON(url, init){
    var method = (init && init.method ? String(init.method).toUpperCase() : 'GET');
    if (method === 'GET' && /\/cart\.js(\?|$)/.test(url)){
      inflightInternal++;
      return window.CHUUG_CART_CACHE.get()
        .finally(function(){ inflightInternal--; });
    }
    inflightInternal++;
    return fetch(url, Object.assign({ credentials: 'same-origin', headers: { 'Accept': 'application/json' } }, init || {}))
      .then(function(r){
        if (!r.ok) throw new Error('HTTP ' + r.status + ' on ' + url);
        return r.json();
      })
      .finally(function(){ inflightInternal--; });
  }

  function postJSON(url, body){
    // Cart mutations invalidate the cached /cart.js GET (audit #2).
    if (window.CHUUG_CART_CACHE && /\/cart\/(add|change|update|clear)/.test(url)){
      try { window.CHUUG_CART_CACHE.bust(); } catch(_){}
    }
    return fetchJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function dispatchCartUpdated(){
    try { document.dispatchEvent(new Event('cart:updated')); } catch(e){}
  }

  // Normalise a PERSONALISED COIN property value into a letter (or null
  // if the vessel doesn't want a medallion).
  function normaliseCoinValue(v){
    if (v == null) return null;
    var s = String(v).trim().toUpperCase();
    if (!s) return null;
    if (s === 'NONE' || s === 'STANDARD') return null;
    return s;
  }

  // Derive the letter for a medallion cart line.
  //   1. Prefer the variant_id ↔ letter reverse map (authoritative).
  //   2. Fallback: read the PERSONALISED COIN line-item property on the
  //      medallion line itself (BYC ATC writes this so the line title
  //      shows the letter at checkout).
  //   3. Final fallback: parse the variant title.
  function letterForMedallionLine(it){
    if (!it) return null;
    var vid = it.variant_id != null ? String(it.variant_id) : '';
    if (vid && LETTER_BY_VARIANT[vid]) return LETTER_BY_VARIANT[vid];
    if (it.properties) {
      var v = normaliseCoinValue(it.properties['PERSONALISED COIN']);
      if (v) return v;
    }
    if (it.variant_title) {
      var m = String(it.variant_title).trim().toUpperCase();
      // variant titles look like "A", "DAD", "Personalised - A", etc.
      var match = m.match(/\b([A-Z]|DAD)\b/);
      if (match) return match[1];
    }
    return null;
  }

  // Identify whether a cart line is a "vessel" (anything that can carry
  // a medallion companion). We use a permissive definition: any line that
  // is NOT a medallion line AND NOT obviously a giftbox add-on.
  // Gift box variant IDs come from the theme-wide constants.
  //
  // ⚠️ Shopify's /cart.js Ajax API exposes the product handle as `handle`
  // at the line item's TOP LEVEL — there is NO `it.product` object. The
  // v3/v4 and original v5 code read `it.product.handle` which was always
  // undefined, causing `isVesselLine()` to short-circuit `false` on every
  // line and `letterForMedallionLine()` checks to also fail. Result: the
  // planner saw 0 vessels + 0 medallions in every cart and removed nothing.
  // Fixed 2026-06-11 PM by Charlie after live-cart diagnostic showed 11
  // unconfirmed items, with every `i.product_handle` JSON dump returning
  // `undefined`.
  var GIFTBOX_VARIANT_IDS = { '55468959531387': 1, '56240425238907': 1 };
  function lineHandle(it){
    if (!it) return '';
    if (typeof it.handle === 'string' && it.handle) return it.handle;
    if (it.product && typeof it.product.handle === 'string') return it.product.handle;
    return '';
  }
  function isMedallionLine(it){
    return lineHandle(it) === 'personalised-medallion';
  }
  function isVesselLine(it){
    if (!it) return false;
    if (isMedallionLine(it)) return false;
    if (GIFTBOX_VARIANT_IDS[String(it.variant_id)]) return false;
    var h = lineHandle(it);
    if (!h) return false;
    return true;
  }

  // Plan: returns { wanted, actual, mutations: [{key, letter, qty}] }.
  //
  // CRITICAL: Shopify's cart API does NOT reliably support targeting cart
  // lines by variant_id when multiple lines share that variant_id (which
  // happens any time the same medallion letter appears on multiple cart
  // lines with different `_bundled_with` properties — e.g. two DAD lines
  // bound to two different vessels). POST /cart/change.js {id: variantId}
  // returns 400 Bad Request in production whenever there's ambiguity.
  //
  // The robust path is POST /cart/update.js {updates: {key: qty, ...}}
  // using the line item KEY (the long colon-suffixed string Shopify
  // returns on every cart line). Each key is unique per line, so we can
  // surgically target individual orphan lines and ask Shopify to zero
  // them. This is what /cart/update.js is designed for.
  //
  // Plan algorithm:
  //   1. Build wanted[letter] = total qty across vessel lines.
  //   2. Walk medallion lines in cart order. For each line, decide how
  //      many of its qty are "covered" by remaining wanted budget for
  //      that letter. The rest is orphan; record a mutation for that
  //      specific line key to drop it by the orphan amount.
  //   3. If a medallion line covers fully (qty <= remaining want), keep
  //      it whole and deduct from the budget. If it exceeds the budget,
  //      trim its qty down. If the budget is already 0, drop the entire
  //      line (qty -> 0).
  function planCleanup(cart){
    var wanted = Object.create(null);
    var actual = Object.create(null);
    var vesselCount = 0;
    var vesselWithCoinCount = 0;
    var medallionLines = []; // [{key, letter, qty, lineIndex}]

    (cart.items || []).forEach(function(it, idx){
      if (isVesselLine(it)) {
        vesselCount += (it.quantity || 0);
        var letter = it.properties ? normaliseCoinValue(it.properties['PERSONALISED COIN']) : null;
        if (letter) {
          vesselWithCoinCount += (it.quantity || 0);
          wanted[letter] = (wanted[letter] || 0) + (it.quantity || 0);
        }
      } else if (isMedallionLine(it)) {
        var ml = letterForMedallionLine(it);
        var key = it.key;
        if (ml) {
          actual[ml] = (actual[ml] || 0) + (it.quantity || 0);
        } else {
          actual['??'] = (actual['??'] || 0) + (it.quantity || 0);
        }
        medallionLines.push({
          key: key,
          letter: ml || '??',
          qty: it.quantity || 0,
          lineIndex: idx
        });
      }
    });

    // Walk medallion lines in cart order; decide trim per line.
    var budget = Object.create(null);
    Object.keys(wanted).forEach(function(l){ budget[l] = wanted[l]; });

    var mutations = [];
    medallionLines.forEach(function(line){
      var letter = line.letter;
      var have = line.qty;
      var remaining = budget[letter] || 0;
      if (have <= remaining) {
        // Fully covered — keep entire line, deduct from budget.
        budget[letter] = remaining - have;
      } else {
        // Trim down to remaining budget (may be 0 = remove the whole line).
        var keepQty = remaining;
        budget[letter] = 0;
        mutations.push({
          key: line.key,
          letter: letter,
          fromQty: have,
          toQty: keepQty
        });
      }
    });

    return {
      wanted: wanted,
      actual: actual,
      vesselCount: vesselCount,
      vesselWithCoinCount: vesselWithCoinCount,
      mutations: mutations
    };
  }

  // Execute a planned mutation list. Batched into a SINGLE
  // POST /cart/update.js call — Shopify processes the entire updates
  // object atomically, which (a) is faster, (b) avoids partial-state
  // races during multi-step trims, and (c) correctly handles multiple
  // lines sharing a variant_id (since we're keyed by line `key`, not
  // variant id). Returns total removed quantity.
  function executePlan(mutations){
    if (!mutations.length) return Promise.resolve(0);
    var updates = {};
    var totalRemoved = 0;
    mutations.forEach(function(m){
      if (!m.key) {
        console.warn('[medallion-orphan-sync] mutation missing line key, skipping:', m);
        return;
      }
      updates[m.key] = m.toQty;
      totalRemoved += (m.fromQty - m.toQty);
      console.info('[medallion-orphan-sync] planning update for letter ' + m.letter + ' (key ' + m.key + '): ' + m.fromQty + ' → ' + m.toQty);
    });
    if (!Object.keys(updates).length) return Promise.resolve(0);
    return postJSON('/cart/update.js', { updates: updates })
      .then(function(){
        console.info('[medallion-orphan-sync] update.js OK; removed ' + totalRemoved + ' orphan qty in one call');
        return totalRemoved;
      })
      .catch(function(err){
        console.warn('[medallion-orphan-sync] update.js failed:', err && err.message);
        return 0;
      });
  }

  // Charlie 2026-06-15 (Mike directive, order #466175): scrub Gift
  // Message off any medallion line. The customer's gift message belongs
  // on the parent CHUUG vessel only; if a positional-line race in the
  // cart-page textarea handler (now fixed by switching to line keys)
  // ever lands it on a medallion line in an in-flight cart, this pass
  // strips it cleanly before checkout. Idempotent and runs as the first
  // step of doSync below.
  function scrubMedallionGiftMessages(cart){
    var fixes = [];
    (cart.items || []).forEach(function(it){
      if (!isMedallionLine(it)) return;
      var props = it.properties || {};
      if (!('Gift Message' in props)) return;
      var msg = props['Gift Message'];
      if (msg == null || msg === '') return; // already blank — nothing to do
      // Rebuild a clean property bag without Gift Message.
      var repaired = {};
      Object.keys(props).forEach(function(k){
        if (k === 'Gift Message') return;
        if (props[k] != null && props[k] !== '') repaired[k] = props[k];
      });
      fixes.push({ key: it.key, properties: repaired });
    });
    if (!fixes.length) return Promise.resolve(0);
    console.info('[medallion-orphan-sync] scrubbing Gift Message off ' + fixes.length + ' medallion line(s)');
    var p = Promise.resolve();
    fixes.forEach(function(f){
      p = p.then(function(){
        return postJSON('/cart/change.js', { id: f.key, properties: f.properties })
          .catch(function(err){
            console.warn('[medallion-orphan-sync] gift-message scrub failed for key', f.key, err);
          });
      });
    });
    return p.then(function(){ return fixes.length; });
  }

  // ── The actual sync ───────────────────────────────────────────────────
  function doSync(){
    if (syncing) { syncQueued = true; return Promise.resolve(0); }
    syncing = true;
    syncQueued = false;

    var p = fetchJSON('/cart.js')
      .then(function(cart){
        // Scrub stray Gift Message off medallion lines first (Mike
        // directive 2026-06-15). Re-fetch the cart so the qty planner
        // works on fresh state if anything got mutated.
        return scrubMedallionGiftMessages(cart).then(function(scrubbed){
          if (scrubbed > 0) return fetchJSON('/cart.js');
          return cart;
        });
      })
      .then(function(cart){
        var plan = planCleanup(cart);
        console.info('[medallion-orphan-sync] doSync stats:',
          'vesselQty=' + plan.vesselCount,
          'vesselWantingMedallion=' + plan.vesselWithCoinCount,
          'wanted=' + JSON.stringify(plan.wanted),
          'actual=' + JSON.stringify(plan.actual),
          'mutationsPlanned=' + plan.mutations.length
        );
        if (!plan.mutations.length) return 0;
        return executePlan(plan.mutations);
      })
      .then(function(removedCount){
        if (removedCount > 0) {
          dispatchCartUpdated();
          // Charlie 2026-06-11 PM (Mike P0 dropdown bug): if an external
          // cart-mutation chain is in flight (e.g. main-cart.liquid's
          // in-cart perso-prompt dropdown handler is mid-way through its
          // change.js → add.js → reload sequence), DO NOT reload from
          // here. Doing so aborts the external code's in-flight fetches
          // and the customer ends up with neither the COIN property set
          // nor the medallion line added — they'd have to click the
          // dropdown a second time for it to stick. The external caller
          // (which is holding the lock via beginExternalSwap) will
          // location.reload() itself once its chain completes, picking
          // up our trimmed state in the same page-load.
          if (inflightInternal > 0) {
            console.info('[medallion-orphan-sync] skipping reload — external swap in progress (inflightInternal=' + inflightInternal + ')');
            return removedCount;
          }
          // Reload on cart page only — other pages will re-pick-up the
          // cleaned cart on their next render naturally.
          if (/^\/cart(\b|\/|$)/.test(window.location.pathname || '')) {
            try {
              var k = 'chuug-medallion-orphan-reloaded';
              if (!window.sessionStorage.getItem(k)) {
                window.sessionStorage.setItem(k, '1');
                setTimeout(function(){ window.location.reload(); }, 60);
              }
            } catch(e){
              setTimeout(function(){ window.location.reload(); }, 60);
            }
          }
        } else if (/^\/cart(\b|\/|$)/.test(window.location.pathname || '')) {
          try { window.sessionStorage.removeItem('chuug-medallion-orphan-reloaded'); } catch(e){}
        }
        return removedCount;
      })
      .catch(function(err){
        console.warn('[medallion-orphan-sync] sync failed:', err);
        return 0;
      })
      .finally(function(){
        syncing = false;
        if (syncQueued) setTimeout(scheduleSync, 0);
      });

    return p;
  }

  function scheduleSync(){
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(doSync, DEBOUNCE_MS);
  }

  function syncNow(){
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
    return doSync().then(function(n){
      if (syncQueued) return doSync().then(function(m){ return n + m; });
      return n;
    });
  }
  window.CHUUG_MEDALLION_ORPHAN_SYNC.syncNow = syncNow;

  // cleanupNow(): callable from BYC ATC, Secure Checkout submit, express
  // checkout handlers. Returns a Promise that resolves AFTER any orphans
  // have been trimmed (or immediately if none). Single-flight so multiple
  // concurrent callers share one in-flight cleanup.
  //
  // IMPORTANT: this NEVER reloads — it's intended to be awaited and
  // followed by an explicit navigation (or a manual reload by the
  // cart-page IIFE).
  // Run plan/execute up to MAX_PASSES times. Needed because /cart/change.js
  // with {id: variantId} only modifies the FIRST cart line containing that
  // variant — if the same letter is spread across multiple lines (different
  // properties) we need another pass to mop up the remaining lines after
  // the first collapses. Each pass refetches /cart.js so the plan reflects
  // the latest state.
  function cleanupPass(passNum, totalRemoved){
    return fetchJSON('/cart.js').then(function(cart){
      var plan = planCleanup(cart);
      console.info('[medallion-orphan-sync] cleanupNow pass ' + passNum + ' stats:',
        'vesselQty=' + plan.vesselCount,
        'vesselWantingMedallion=' + plan.vesselWithCoinCount,
        'wanted=' + JSON.stringify(plan.wanted),
        'actual=' + JSON.stringify(plan.actual),
        'mutationsPlanned=' + plan.mutations.length
      );
      if (!plan.mutations.length) return totalRemoved;
      return executePlan(plan.mutations).then(function(removed){
        var grand = totalRemoved + removed;
        if (passNum >= 4) {
          console.warn('[medallion-orphan-sync] cleanupNow: reached max passes (' + passNum + '); stopping with ' + grand + ' removed');
          return grand;
        }
        return cleanupPass(passNum + 1, grand);
      });
    });
  }

  function cleanupNow(){
    if (inflightCleanup) return inflightCleanup;
    inflightCleanup = (function(){
      // Drain any pending debounced sync, then do an explicit pass that
      // does NOT trigger a reload (we'll let the caller navigate instead).
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
      var startedAt = Date.now();
      return cleanupPass(1, 0)
        .then(function(removedCount){
          console.info('[medallion-orphan-sync] cleanupNow done in ' + (Date.now() - startedAt) + 'ms; trimmed ' + removedCount + ' orphan medallion(s) total');
          if (removedCount > 0) dispatchCartUpdated();
          return removedCount;
        })
        .catch(function(err){
          console.warn('[medallion-orphan-sync] cleanupNow failed:', err);
          return 0;
        });
    })().finally(function(){ inflightCleanup = null; });
    return inflightCleanup;
  }

  // ── Monkey-patch fetch to catch any cart mutation ─────────────────────
  (function(){
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return;
    var RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    window.fetch = function(input, init){
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch(e){}
      var p = origFetch.apply(this, arguments);
      // Bust the shared /cart.js dedupe cache after any cart mutation (audit #2).
      if (url && RE.test(url) && window.CHUUG_CART_CACHE){
        p.then(function(){ try { window.CHUUG_CART_CACHE.bust(); } catch(_){} }, function(){});
      }
      if (!isInternalFetch() && url && RE.test(url)){
        p.then(function(){ scheduleSync(); }, function(){});
      }
      return p;
    };
  })();

  // ── On page load, sync once ───────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
})();
